import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils';
import { Input } from './Input';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  scrollable?: boolean;
}

/**
 * IUI Modal Component
 * 
 * Smooth, non-intrusive modal with backdrop blur.
 * Follows IUI design principles for seamless integration.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  scrollable = false,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 modal-backdrop animate-fade-in"
      onClick={closeOnOverlayClick ? (e) => e.target === overlayRef.current && onClose() : undefined}
      role="dialog"
      aria-modal="true"
    >
      {/* Solid dialog — blur applies only to the full-screen layer behind this */}
      <div
        className={cn(
          'relative w-full modal-panel rounded-2xl',
          'animate-scale-in',
          sizes[size],
          scrollable && 'max-h-[min(90vh,44rem)] flex flex-col overflow-hidden'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || description) && (
          <div className="shrink-0 px-6 pt-6 pb-2 border-b border-khiimori-border/40">
            {title && (
              <h2 className="text-lg font-semibold text-khiimori-text-primary pr-8">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-2 text-sm text-khiimori-text-secondary leading-relaxed">
                {description}
              </p>
            )}
          </div>
        )}
        
        {/* Content */}
        <div
          className={cn(
            'px-6 py-5',
            !(title || description) && 'pt-6',
            scrollable && 'flex-1 min-h-0 overflow-y-auto overscroll-contain'
          )}
        >
          {children}
        </div>

        {footer && (
          <div className="shrink-0 px-6 pb-6 pt-2 border-t border-khiimori-border/40">
            {footer}
          </div>
        )}
        
        {/* Close button */}
        <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-tertiary transition-colors duration-150"
          aria-label="Close modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  );
}

// Confirm Dialog variant
export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  isLoading = false,
}: ConfirmDialogProps) {
  const iconColors = {
    danger: 'text-error-500',
    warning: 'text-warning-500',
    info: 'text-primary-500',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col items-center text-center">
        {/* Icon */}
        <div className={cn('mb-4 p-3 rounded-full bg-khiimori-bg-elevated/50', iconColors[variant])}>
          {variant === 'danger' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {variant === 'warning' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          )}
          {variant === 'info' && (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          )}
        </div>

        {/* Text */}
        <h3 className="text-lg font-semibold text-khiimori-text-primary mb-2">{title}</h3>
        <p className="text-sm text-khiimori-text-primary/50 mb-6">{message}</p>

        {/* Actions */}
        <div className="flex gap-3 w-full">
          <button
            onClick={onClose}
            className="flex-1 h-10 px-4 rounded-md border border-khiimori-border text-khiimori-text-primary font-medium hover:bg-khiimori-bg-elevated/50 active:bg-khiimori-bg-elevated/80 transition-colors duration-150"
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={cn(
              'flex-1 h-10 px-4 rounded-md font-medium text-white transition-all duration-150 active:scale-[0.98]',
              variant === 'danger' && 'bg-error-500 hover:bg-error-600 active:bg-error-700',
              variant === 'warning' && 'bg-warning-500 hover:bg-warning-600 active:bg-warning-700',
              variant === 'info' && 'bg-primary-500 hover:bg-primary-600 active:bg-primary-700'
            )}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export interface PromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  description?: string;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  submitText?: string;
  cancelText?: string;
  required?: boolean;
}

export function PromptDialog({
  isOpen,
  onClose,
  onSubmit,
  title,
  description,
  label,
  defaultValue = '',
  placeholder,
  submitText = 'Save',
  cancelText = 'Cancel',
  required = true,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      setError(undefined);
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
  }, [isOpen, defaultValue]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (required && !trimmed) {
      setError('This field is required');
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          ref={inputRef}
          label={label}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(undefined);
          }}
          placeholder={placeholder}
          error={error}
          className="[&_label]:text-khiimori-text-secondary [&_input]:border-khiimori-border [&_input]:bg-khiimori-bg-elevated/50 [&_input]:text-khiimori-text-primary [&_input]:placeholder:text-khiimori-text-muted"
        />
        <div className="flex gap-3 w-full">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 px-4 rounded-md border border-khiimori-border text-khiimori-text-primary font-medium hover:bg-khiimori-bg-elevated/50 active:bg-khiimori-bg-elevated/80 transition-colors duration-150"
          >
            {cancelText}
          </button>
          <button
            type="submit"
            className="flex-1 h-10 px-4 rounded-md font-medium text-white bg-primary-500 hover:bg-primary-600 active:bg-primary-700 transition-all duration-150 active:scale-[0.98]"
          >
            {submitText}
          </button>
        </div>
      </form>
    </Modal>
  );
}